import React, { createContext, useContext, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { authSelector } from '../../store/slices/auth';
import { getNotesInfo } from './services';

const NotesContext = createContext();

export const useNotes = () => {
  const context = useContext(NotesContext);
  if (!context) {
    throw new Error('useNotes must be used within a NotesProvider');
  }
  return context;
};

export const NotesProvider = ({ children }) => {
  const { userInfo } = useSelector(authSelector);
  const [notesCache, setNotesCache] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const [pendingRequests, setPendingRequests] = useState({});

  const getNotesForClient = useCallback(
    async (clientId, contentGuideline = false) => {
      const cacheKey = `${clientId}_${contentGuideline}`;

      // Return cached data if available
      if (notesCache[cacheKey]) {
        return notesCache[cacheKey];
      }

      // If there's already a pending request for this key, wait for it
      if (pendingRequests[cacheKey]) {
        return pendingRequests[cacheKey];
      }

      // Set loading state
      setLoadingStates((prev) => ({ ...prev, [cacheKey]: true }));

      // Create a promise for this request
      const requestPromise = getNotesInfo(
        clientId,
        userInfo.accessToken,
        contentGuideline
      )
        .then((notesData) => {
          // Cache the result
          setNotesCache((prev) => ({ ...prev, [cacheKey]: notesData }));
          return notesData;
        })
        .catch((error) => {
          console.error('Error fetching notes:', error);
          return null;
        })
        .finally(() => {
          // Clear loading state and pending request
          setLoadingStates((prev) => ({ ...prev, [cacheKey]: false }));
          setPendingRequests((prev) => {
            const newPending = { ...prev };
            delete newPending[cacheKey];
            return newPending;
          });
        });

      // Store the pending request
      setPendingRequests((prev) => ({ ...prev, [cacheKey]: requestPromise }));

      return requestPromise;
    },
    [userInfo.accessToken, notesCache, loadingStates, pendingRequests]
  );

  const clearNotesCache = useCallback(
    (clientId = null) => {
      if (clientId) {
        // Clear cache for specific client
        const newCache = { ...notesCache };
        Object.keys(newCache).forEach((key) => {
          if (key.startsWith(`${clientId}_`)) {
            delete newCache[key];
          }
        });
        setNotesCache(newCache);
      } else {
        // Clear all cache
        setNotesCache({});
      }
    },
    [notesCache]
  );

  const isNotesLoading = useCallback(
    (clientId, contentGuideline = false) => {
      const cacheKey = `${clientId}_${contentGuideline}`;
      return loadingStates[cacheKey] || false;
    },
    [loadingStates]
  );

  const value = {
    getNotesForClient,
    clearNotesCache,
    isNotesLoading,
    notesCache
  };

  return (
    <NotesContext.Provider value={value}>{children}</NotesContext.Provider>
  );
};
